// ✅ DROP-IN REPLACEMENT WITH IMMEDIATE EMAIL VERIFICATION
// - Sends verification email immediately after signup (password auth only)
// - Routes to /verify-email instead of /complete-account-setup for unverified users
// - Social signups (already verified) go directly to setup

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../firebase.js";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase.js";
import { getEmailVerificationActionCodeSettings } from "../../../auth/authState.js";
import {
  createOwnerProfileRecords,
  safeTrimEmail,
  safeTrimName,
} from "../../../auth/ownerProfile.js";

// ------------------------
// Password strength helper
// ------------------------
function getPasswordStrength(password) {
  let score = 0;

  if (!password) return { score: 0, label: "Too short" };

  const length = password.length;
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);

  if (length >= 8) score++;
  if (length >= 12) score++;
  if (hasLower && hasUpper) score++;
  if (hasNumber) score++;
  if (hasSymbol) score++;

  let label = "Weak";
  if (score <= 1) label = "Weak";
  else if (score === 2) label = "Fair";
  else if (score === 3) label = "Good";
  else label = "Strong";

  return { score, label };
}

export default function SignUp() {
  const navigate = useNavigate();

  // Form fields
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Password UX
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: "" });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Status
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordTouched(true);
    setPasswordStrength(getPasswordStrength(value));
  };

  const isPasswordStrongEnough = password.length >= 8 && passwordStrength.score >= 3;

  const strengthScore = Math.min(passwordStrength.score, 4);
  const strengthColorClass = useMemo(() => {
    let cls = "bg-gray-300 text-gray-600";
    if (passwordTouched && password.length > 0) {
      if (strengthScore <= 1) cls = "bg-red-100 text-red-700";
      else if (strengthScore === 2) cls = "bg-yellow-100 text-yellow-700";
      else if (strengthScore === 3) cls = "bg-green-100 text-green-700";
      else cls = "bg-emerald-100 text-emerald-700";
    }
    return cls;
  }, [passwordTouched, password.length, strengthScore]);

  const handleReturnToLogin = () => {
    navigate("/login", { replace: true });
  };

  // ========================================================
  // Social Sign-Up Handler
  // ========================================================
  const handleSocialSignUp = async (providerType) => {
    let provider;

    if (providerType === "google") {
      provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
    } else if (providerType === "microsoft") {
      provider = new OAuthProvider("microsoft.com");
    } else {
      setError("Unknown provider.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      console.log(`[SignUp] Starting ${providerType} OAuth sign-up`);

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const trimmedFullName = safeTrimName(user.displayName || "");

      const userDocRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userDocRef);

      if (userSnap.exists()) {
        console.log("[SignUp] Existing user (OAuth) — redirecting.");
        navigate("/", { replace: true });
        return;
      }

      await createOwnerProfileRecords({
        user,
        fullName: trimmedFullName,
        phone: "",
      });

      console.log("[SignUp] ✅ OAuth signup complete — sending to onboarding.");
      navigate("/complete-account-setup", { replace: true });
    } catch (err) {
      console.error(`[SignUp] ${providerType} OAuth error:`, err);

      if (err.code === "auth/popup-closed-by-user") setError("Sign-up cancelled. Please try again.");
      else if (err.code === "auth/account-exists-with-different-credential") {
        setError(
          "An account already exists with this email using a different sign-in method. " +
            "Please try logging in with your original method."
        );
      } else if (err.code === "auth/popup-blocked") {
        setError("Pop-up was blocked by your browser. Please allow pop-ups for this site and try again.");
      } else {
        setError(err?.message || `Unable to sign up with ${providerType}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // Email/Password Sign-Up Handler
  // ========================================================
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");

    if (!isPasswordStrongEnough) {
      setPasswordTouched(true);
      setError(
        "Please choose a stronger password. Aim for at least 8+ characters with upper/lowercase, a number, and a symbol."
      );
      return;
    }

    const trimmedEmail = safeTrimEmail(email);
    const trimmedFullName = safeTrimName(fullName);

    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      // 1. Create auth user
      const { user } = await createUserWithEmailAndPassword(auth, trimmedEmail, password);

      console.log("[SignUp] ✅ Created auth user:", { uid: user.uid, email: user.email });

      // 2. Send verification email IMMEDIATELY
      await sendEmailVerification(user, getEmailVerificationActionCodeSettings());
      console.log("[SignUp] Verification email sent to:", trimmedEmail);

      // 3. Create Firestore user doc + company workspace
      await createOwnerProfileRecords({
        user,
        fullName: trimmedFullName,
        phone: (phone || "").trim(),
      });

      // 4. Redirect to verification page (NOT to setup)
      console.log("[SignUp] ✅ Signup complete — redirecting to verification.");
      navigate("/verify-email", { replace: true });
      
    } catch (err) {
      console.error("[SignUp] ❌ Signup error:", err);

      if (err?.code === "auth/email-already-in-use") {
        setError("That email is already in use. Please log in instead, or use a different email.");
      } else if (err?.code === "auth/invalid-email") {
        setError("That email address is invalid. Please check and try again.");
      } else if (err?.code === "auth/weak-password") {
        setError("That password is too weak. Please choose a stronger password and try again.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setError(err?.message || "Unable to create your account.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 flex items-center justify-center px-4">
      <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="text-slate-50 hidden lg:block">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-400/30 text-xs font-medium mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Sample delivery workflows, without the chaos.
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold mb-4">
            Create your <span className="text-sky-300">SupplyHawk</span> admin account.
          </h1>
          <p className="text-slate-200/80 text-sm leading-relaxed mb-6 max-w-md">
            One login to manage samples, follow-ups, and conversions to orders — across your whole sales team.
          </p>
        </div>

        <div className="bg-white/95 rounded-2xl shadow-2xl shadow-sky-900/40 border border-slate-200/70 backdrop-blur-sm p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-1 text-center">
            Create your admin login
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mb-6 text-center">
            This will be the primary owner account for your company.
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleSocialSignUp("google")}
              disabled={loading}
              type="button"
              className="w-full flex items-center justify-center py-3 px-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-6 h-6 mr-3"
              />
              <span className="font-medium text-slate-700 text-sm">Sign up with Google</span>
            </button>

            <button
              onClick={() => handleSocialSignUp("microsoft")}
              disabled={loading}
              type="button"
              className="w-full flex items-center justify-center py-3 px-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                alt="Microsoft"
                className="w-6 h-6 mr-3"
              />
              <span className="font-medium text-slate-700 text-sm">Sign up with Microsoft</span>
            </button>
          </div>

          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-slate-200" />
            <span className="mx-4 text-slate-500 text-xs">OR</span>
            <div className="flex-grow border-t border-slate-200" />
          </div>

          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="font-medium text-sm text-slate-800">Full Name</label>
              <input
                type="text"
                className="border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-2 text-sm"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-medium text-sm text-slate-800">Phone Number</label>
              <input
                type="tel"
                className="border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-2 text-sm"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="font-medium text-sm text-slate-800">Work Email</label>
              <input
                type="email"
                className="border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-2 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="font-medium text-sm text-slate-800">Password</label>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-xs text-sky-600 hover:text-sky-700"
                  disabled={loading}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none rounded-md px-3 py-2 text-sm"
                value={password}
                onChange={handlePasswordChange}
                onBlur={() => setPasswordTouched(true)}
                placeholder="Create a strong password"
                required
                disabled={loading}
              />
              {passwordTouched && password && !loading && (
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium ${strengthColorClass}`}>
                  <span className="mr-1">Password strength:</span>
                  <span>{passwordStrength.label}</span>
                </div>
              )}
            </div>

            <button
              disabled={loading || !isPasswordStrongEnough}
              type="submit"
              className="mt-2 w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 disabled:cursor-not-allowed text-white py-2.5 rounded-full text-sm font-semibold"
            >
              {loading ? "Creating account..." : "Create account & continue"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              Already have an account?{" "}
              <button
                onClick={handleReturnToLogin}
                className="text-sky-600 hover:text-sky-800 font-medium underline-offset-2 hover:underline"
              >
                Log in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
